import { useSensorEvents } from "common";
import { useEffect, useState } from "react";

import { useAppSync } from "../../contexts/AppSyncContext";

export interface HumidityDataItem {
  value: number;
  label: string;
  error?: boolean;
  timestamp?: number;
}

const HIGH_HUMIDITY_THRESHOLD = 61;

export const useHumidity = (sensorId?: string) => {
  const { selectedHub } = useAppSync();

  const { fetchSensorHistoricalState } = useSensorEvents(
    selectedHub?.id || undefined,
  );

  const [humidityData, setHumidityData] = useState<HumidityDataItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!sensorId) {
      setLoading(false);
      return;
    }

    const fetchHumidityData = async () => {
      setLoading(true);
      setError(null);

      try {
        const rollupForOneHour = 60 * 60;
        const endTime = Math.floor(Date.now() / 1000);
        const startTime = endTime - 7 * 24 * rollupForOneHour;

        const response = await fetchSensorHistoricalState(
          sensorId,
          startTime,
          endTime,
          rollupForOneHour,
        );

        const historicalStates = response || [];
        historicalStates.sort(
          (a: any, b: any) => (a.timestamp ?? 0) - (b.timestamp ?? 0),
        );

        const dayMap = new Map<string, HumidityDataItem[]>();

        for (let i = 6; i >= 0; i--) {
          const date = new Date();
          date.setDate(date.getDate() - i);
          const dayLabel = date.toLocaleDateString("en-US", {
            weekday: "short",
          });
          dayMap.set(dayLabel, []);
        }

        historicalStates.forEach((item: any) => {
          let humidityValue = 0;
          if (item.values && !isNaN(parseFloat(item.values.humidity))) {
            humidityValue = Math.min(parseFloat(item.values.humidity), 100);
          }

          const date = new Date((item.timestamp as number) * 1000);
          const dayLabel = date.toLocaleDateString("en-US", {
            weekday: "short",
          });

          if (dayMap.has(dayLabel)) {
            dayMap.get(dayLabel)?.push({
              value: humidityValue,
              label: dayLabel,
              error: humidityValue >= HIGH_HUMIDITY_THRESHOLD,
              timestamp: item.timestamp,
            });
          }
        });

        const finalChartData: HumidityDataItem[] = [];

        dayMap.forEach((dataPoints, dayLabel) => {
          if (dataPoints.length === 0) {
            finalChartData.push({
              value: 0,
              label: dayLabel,
              error: false,
            });
          } else {
            const latestPoint = dataPoints.sort(
              (a, b) => (b.timestamp ?? 0) - (a.timestamp ?? 0),
            )[0];
            finalChartData.push({
              value: latestPoint.value,
              label: dayLabel,
              error: latestPoint.error,
            });
          }
        });

        setHumidityData(finalChartData);
      } catch (err) {
        console.error("Error fetching humidity data:", err);
        setError(err as Error);

        const emptyData: HumidityDataItem[] = [];
        for (let i = 6; i >= 0; i--) {
          const date = new Date();
          date.setDate(date.getDate() - i);
          emptyData.push({
            value: 0,
            label: date.toLocaleDateString("en-US", { weekday: "short" }),
            error: false,
          });
        }

        setHumidityData(emptyData);
      } finally {
        setLoading(false);
      }
    };

    fetchHumidityData();
  }, [sensorId]);

  return {
    humidityData,
    loading,
    error,
  };
};
